/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { Injectable } from '@angular/core';
import { AbstractCanvasConnection } from './AbstractCanvasConnection';
import { UserPreferenceStateService } from '../../../services/user-preference-state.service';
import { CrewSizeApplication } from '../../../plain-javascript/crew-sizes/CrewSizeApplication';
import { ExportCapture, ICapturedTile, ITileOffset } from '@appCanvasCommon/export/ExportCapture';
import { Rectangle } from 'pixi.js-legacy';
/**
 * Interface defining the data structure for crew size visibility state
 */
export interface ICrewSizeServiceData {
	/**
	 * Indicates whether crew size labels should be visible on the canvas
	 */
	isVisible: boolean;
}
/**
 * Service responsible for managing the visibility and rendering of crew size application
 * This service is a connector between Angular and the CrewSizeApplication canvasland
 */
@Injectable({ providedIn: 'root' })
export class CrewSizeService extends AbstractCanvasConnection<ICrewSizeServiceData> {
	/**
	 * Reference to the CrewSizeApplication that handles crew size rendering
	 * @private
	 */
	private _application: CrewSizeApplication | null = null;
	/**
	 * Cached state of crew size visibility
	 * @private
	 */
	private _lastVisible: boolean = false;

	/**
	 * Gets the crew size application exporter
	 */
	public get exporter(): ExportCapture | null {
		return this._application ? this._application.exporter : null;
	}

	/**
	 * Returns the currently connecting CrewSizeApplication
	 */
	public get application(): CrewSizeApplication | null {
		return this._application;
	}

	/**
	 * Creates an instance of CrewSizeService
	 * @param userPreferenceService - Service for accessing user preferences
	 */
	constructor(private userPreferenceService: UserPreferenceStateService) {
		super();
	}
	/**
	 * Registers the crew size application with the service. This is invoked by the `CrewSizeComponent`
	 * @param crewSizeApplication - The CrewSizeApplication instance to connect to
	 * @override
	 */
	public override registerPlanData(crewSizeApplication: CrewSizeApplication): void {
		super.registerPlanData();
		this._application = crewSizeApplication;
		this.setCrewSizesVisibility(this.userPreferenceService.showLaborCount);
		this._subscription = this.userPreferenceService.showLaborCount$.subscribe((value) =>
			this.setCrewSizesVisibility(value)
		);
	}
	/**
	 * Sets the visibility state of crew size labels and notifies subscribers of the change
	 * @param isVisible - Whether crew size labels should be visible
	 */
	public setCrewSizesVisibility(isVisible: boolean): void {
		if (this._lastVisible === isVisible) {
			return;
		}
		this._lastVisible = isVisible;
		this.userPreferenceService.showLaborCount = isVisible;
		this._changed$.next({ isVisible });
	}
	/**
	 * Helper method to show crew size
	 */
	public showCrewSizes(): void {
		this.setCrewSizesVisibility(true);
	}
	/**
	 * Helper method to hide crew size
	 */
	public hideCrewSizes(): void {
		this.setCrewSizesVisibility(false);
	}
	/**
	 * Captures a tile of the crew size layer for export. This is used when exporting or printing the plan.
	 * @param captureArea - Optional rectangle defining the area to capture. If not provided, captures the entire visible area
	 * @returns An ICapturedTile containing the rendered crew size labels, or an empty tile if crew sizes are hidden or application is not initialized
	 */
	public captureTile(captureArea?: Rectangle, offset?: ITileOffset): ICapturedTile {
		if (!this._lastVisible || !this._application) {
			return ExportCapture.createEmptyTile();
		}
		return this._application.capture(captureArea, offset);
	}

	/**
	 * Retrieve all of the previously captured tiles for the CrewSizeApplication
	 * @returns An array of ICapturedTile[]
	 */
	public getCapturedTiles(): ICapturedTile[] {
		if (!this._application || !this._lastVisible) {
			return [];
		}
		return this._application!.capturedTiles;
	}

	/**
	 * Marks the AbstractApplication as dirty and forces a redraw
	 */
	public markAsDirty(): void {
		this._application?.markAsDirty();
	}

	/**
	 * Helper method to check if crew sizes is currently visible or not.
	 */
	public isVisible(): boolean {
		return this._lastVisible;
	}
}
